import {
  Box,
  Card,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

const rules = {
  dateRange: [
    'The report uses Asia/Kolkata (IST). Start and end dates are inclusive.',
    'Selecting a start date automatically selects an end date six days later. The end date remains editable.',
    'Bookings are filtered by serviceDate, not booking createdAt.',
    'Expert, profile, and provider-service additions are filtered by createdAt from 00:00 IST on the start date to 00:00 IST after the end date.',
    'Initiated, in-progress, and test bookings are excluded from the weekly booking population.',
  ],
  reconciliation: [
    'Completed and missed bookings cannot exceed Total Bookings.',
    'Admin plus App/Customer can be lower than Total Bookings when a booking has no recognized source log.',
    'Weekly Expert Earnings should match grouped total_payable_amount for the same service-date range.',
    'Do not add category/city expert rows to reconcile Total Expert Count because one expert can cover multiple categories and cities.',
    'Do not add city service rows to calculate Total Services Added because one expert service can appear under multiple service-area cities.',
  ],
};

const Section = ({ title, children }) => (
  <Box>
    <Typography variant="h6" sx={{ mb: 1, color: 'primary.main', fontWeight: 600 }}>{title}</Typography>
    <Divider sx={{ mb: 2 }} />
    {children}
  </Box>
);

const RuleList = ({ items }) => (
  <Box component="ul" sx={{ mt: 0, mb: 0, pl: 3 }}>
    {items.map((item) => (
      <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: .75 }} key={item}>
        {item}
      </Typography>
    ))}
  </Box>
);

const Metric = ({ name, children }) => (
  <Box sx={{ mb: 2, pl: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{name}</Typography>
    <Typography variant="body2" color="text.secondary">{children}</Typography>
  </Box>
);

const Formula = ({ children }) => (
  <Box
    component="pre"
    sx={{
      m: 0,
      mt: 1,
      p: 1.5,
      bgcolor: 'grey.100',
      borderRadius: 1,
      fontSize: '.75rem',
      lineHeight: 1.6,
      whiteSpace: 'pre-wrap',
      overflowWrap: 'anywhere',
    }}
  >
    {children}
  </Box>
);

const WeeklyOperationsReportGuidelines = () => (
  <Card sx={{ p: { xs: 2, md: 4 }, maxHeight: '74vh', overflowY: 'auto' }}>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Section title="Purpose and Date Range">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The Weekly Operations Report uses one API response for the admin screen, PDF, and CSV. It combines current CuDel expert totals with metrics calculated for the selected date range.
        </Typography>
        <RuleList items={rules.dateRange} />
      </Section>

      <Section title="Weekly Stats">
        <Metric name="Total Expert Count">
          Current approved, non-test, non-deleted CuDel experts. Includes active and deactivated accounts and is not restricted by the selected dates.
        </Metric>
        <Metric name="Active Expert Count">
          Current approved experts where users.isActive is true. This is an account-status flag, not a communication-response score.
        </Metric>
        <Metric name="Experts Added">
          Non-test, non-deleted expert registrations created within the selected dates. This can include pending or rejected registrations.
        </Metric>
        <Metric name="Total Bookings">
          Non-test bookings scheduled within the selected dates, excluding initiated and in-progress bookings.
        </Metric>
        <Metric name="Completed Bookings">
          Weekly bookings whose current bookingStatus is completed.
        </Metric>
        <Metric name="Missed Bookings">
          Weekly bookings that remain requested or accepted, are rejected, or are cancelled by someone other than the customer. Customer cancellations are excluded.
        </Metric>
        <Metric name="Bookings from Admin Panel / App">
          Source comes from the earliest initiated or in-progress state log: creatorRoleId 1 is Admin and creatorRoleId 2 is App/Customer. Missing or different roles remain in Total Bookings but are not displayed in either source count.
        </Metric>
      </Section>

      <Section title="Profile Requirements">
        <Metric name="Profiles Set Up as Required">
          Approved experts created within the selected dates who have profile details, a service-area address, a category, an active service, and at least one working-day time slot.
        </Metric>
        <Metric name="Complete Profiles">
          Approved experts created within the selected dates who pass all eight checks: profile, service-area address, category, active service, working time slot, bank details, business details, and e-KYC.
        </Metric>
        <Typography variant="body2" color="text.secondary">
          These are existence checks for the related records; they do not validate every text field inside those records.
        </Typography>
      </Section>

      <Section title="Weekly Expert Earnings">
        <Typography variant="body2" color="text.secondary">
          Earnings use the grouped payout total_payable_amount calculation. Only completed bookings whose latest payment record is TRANSFERRED_FULL or PAID_PARTIAL contribute earnings. Other completed bookings contribute ₹0 but remain in completed-booking counts.
        </Typography>
        <Formula>{`Net Base = Subtotal + Visiting Charge − Discount − Coin Value − Coupon Amount

GST on Platform Fee = Platform Fee × 18%
TCS = (Net Base − Platform Fee + Adjustment) × 1%

Booking Payable = Net Base − Platform Fee − GST on Platform Fee − TCS + Tip + Adjustment
Weekly Expert Earnings = Sum of Booking Payable`}</Formula>
        <TableContainer sx={{ mt: 2, maxWidth: 520 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Net Base</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Platform Fee</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                ['Up to ₹500', '₹20'],
                ['₹500.01–₹1,000', '₹40'],
                ['₹1,000.01–₹1,500', '₹60'],
                ['₹1,500.01–₹2,500', '₹80'],
                ['Above ₹2,500', '₹100'],
              ].map(([range, fee]) => (
                <TableRow key={range}>
                  <TableCell>{range}</TableCell>
                  <TableCell align="right">{fee}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Section>

      <Section title="Category and Geography Wise Expert Count">
        <RuleList items={[
          'Total and Active Experts are current approved expert coverage counts, not selected-range onboarding counts.',
          'Category uses the parent category when available. Missing mappings display as Uncategorized.',
          'Configured English and Hindi NCR city aliases are combined into Delhi NCR; all non-NCR service-area cities are combined into Other.',
          'Unmapped, blank, numeric, and external administrative geography labels marked as Unorganized are excluded from the displayed table and downloads.',
          'Completed Bookings includes all non-test completed bookings with a service date up to the current IST date.',
          'Missed Bookings includes all non-test requested, accepted, rejected, and non-customer-cancelled bookings with a service date up to the current IST date.',
          'Experts are counted distinctly within each category-geography combination, so multiple NCR service-area cities do not duplicate an expert in Delhi NCR.',
          'An expert can appear in both Delhi NCR and Other or under multiple categories, and a multi-category booking can count in more than one category.',
          'The Overall Total row uses distinct lifetime/current expert and booking totals; it does not sum overlapping category-region rows.',
        ]} />
      </Section>

      <Section title="Top Performing Experts">
        <RuleList items={[
          'Only experts with positive Weekly Earnings in the selected range are candidates; ₹0 earners are excluded.',
          'The report displays a maximum of five experts.',
          'Experts are ranked by Weekly Earnings descending, then Completed Bookings descending, then Expert ID ascending.',
          'Weekly Earnings uses eligible booking payable amounts; Executed and Missed remain booking counts.',
        ]} />
      </Section>

      <Section title="Earnings by Category and Geography">
        <RuleList items={[
          'Category earnings allocate each booking payable across its items in proportion to item price × quantity.',
          'Geography earnings assign the full booking payable to service_bookings.add_city.',
          'Categories and geographies with ₹0 earnings are excluded.',
          'Percentage-of-total columns are not displayed.',
        ]} />
      </Section>

      <Section title="Top 20 Expert-Registered Services by City">
        <RuleList items={[
          'Counts non-deleted provider_services created within the selected IST timestamp range.',
          'The top 20 child services are selected by unique registered experts, then provider-service registrations, within the selected period.',
          'Rows show Category, Subcategory, and Service. Repeated Category and Subcategory values are displayed as merged cells.',
          'Delhi NCR combines configured English and Hindi NCR city aliases into one Delhi NCR column.',
          'Every non-NCR service-area city remains a separate column instead of being grouped as Other.',
          'External administrative geography labels marked as Unorganized are excluded.',
          'Each city cell shows the distinct experts who registered that service and currently have that service-area city.',
          'The matrix Total row sums the displayed top-20 service counts for each city column.',
          'Category uses the parent category and Subcategory uses the mapped child category. Missing mappings are clearly labelled.',
          'The query does not require the newly added provider service to be active.',
          'One expert can contribute to multiple city columns when they currently serve multiple cities, but appears only once in Delhi NCR.',
          'Total Services Added remains the unique overall provider-service count and must not be calculated by summing matrix cells.',
        ]} />
      </Section>

      <Section title="Bookings by Region and Source">
        <RuleList items={[
          'Booking cities are combined into Delhi NCR and Other using the same configured English and Hindi city aliases.',
          'Total, Admin, and App/Customer counts continue to use the selected service-date range and booking-source rules described above.',
        ]} />
      </Section>

      <Section title="Validation and Reconciliation">
        <RuleList items={rules.reconciliation} />
      </Section>

      <Section title="Display and Download Rules">
        <RuleList items={[
          'Information icons show metric definitions in the browser and are excluded from PDF capture.',
          'Currency uses Indian number formatting and the ₹ symbol.',
          'Category names are grouped instead of repeated on every geography row.',
          'The PDF and CSV are generated from the same API response shown on screen.',
        ]} />
      </Section>
    </Box>
  </Card>
);

export default WeeklyOperationsReportGuidelines;
